import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Inventory from "./pages/Inventory";
import Transfer from "./pages/Transfer";
import Hold from "./pages/Hold";
import Usage from "./pages/Usage";
import Audit from "./pages/Audit";
import Profile from "./pages/Profile";
import ServiceTickets from "./pages/ServiceTickets";
import ServiceDeletedTickets from "./pages/ServiceDeletedTickets";
import ServiceTicketDetail from "./pages/ServiceTicketDetail";
import ServiceAudit from "./pages/ServiceAudit";
import MasterDataPage from "./pages/MasterDataPage";
import ManageUsers from "./pages/ManageUsers";
import QuotationHistory from "./pages/QuotationHistory";
import QuotationFormPage from "./pages/QuotationFormPage";
import QuotationCreateChoice from "./pages/QuotationCreateChoice";
import DeletedQuotationHistory from "./pages/DeletedQuotationHistory";
import DeletedQuotationDetail from "./pages/DeletedQuotationDetail";
import QuotationAccessDenied from "./components/quotation/QuotationAccessDenied";
import Firmware from "./pages/Firmware";
import CannibalDevices from "./pages/CannibalDevices";
import Report from "./pages/Report";
import SparePartSummary from "./pages/SparePartSummary";
import MobileApp from "./mobile/MobileApp";
import MobileLogin from "./mobile/MobileLogin";
import MobileInventory from "./mobile/MobileInventory";
import MobileDashboard from "./mobile/MobileDashboard";
import { ViewModeProvider, useViewMode } from "./context/ViewModeContext";

const FIRMWARE_ROLES = ["staff", "admin"];

function DeviceRedirect() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { mode } = useViewMode();

  useEffect(() => {
    const mobileTarget = pathname === "/" ? "/mobile/dashboard" : pathname === "/inventory" ? "/mobile/inventory" : null;
    const desktopTarget = pathname === "/mobile/dashboard" ? "/" : pathname === "/mobile/inventory" ? "/inventory" : null;
    if (mode === "mobile" && mobileTarget) navigate(mobileTarget, { replace: true });
    if (mode === "desktop" && desktopTarget) navigate(desktopTarget, { replace: true });
  }, [mode, navigate, pathname]);

  return null;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <ViewModeProvider><Toaster />
            <DeviceRedirect />
            <Routes>
              <Route path="/login" element={<Login />} />
              {/* Mobile routes */}
              <Route path="/mobile" element={<MobileLogin />} />
              <Route element={<MobileApp />}>
                <Route path="/mobile/dashboard" element={<MobileDashboard />} />
                <Route path="/mobile/inventory" element={<MobileInventory />} />
              </Route>
              {/* Desktop routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Dashboard />} />
                <Route path="/report" element={<ProtectedRoute roles={["staff", "admin"]}><Report /></ProtectedRoute>} />
                <Route path="/summary/spare-part" element={<ProtectedRoute roles={["staff", "admin"]}><SparePartSummary /></ProtectedRoute>} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/transfer" element={<Transfer />} />
                <Route path="/hold" element={<Hold />} />
                <Route path="/usage" element={<Usage />} />
                <Route path="/service" element={<Navigate to="/service/sold" replace />} />
                <Route path="/service/sold" element={<ServiceTickets category="sold" />} />
                <Route path="/service/rental" element={<ServiceTickets category="rental" />} />
                <Route path="/service/stock" element={<ServiceTickets category="stock" />} />
                <Route path="/service/cannibal" element={<ProtectedRoute roles={["staff", "admin"]}><CannibalDevices /></ProtectedRoute>} />
                <Route path="/service/sold/deleted" element={<ProtectedRoute admin><ServiceDeletedTickets category="sold" /></ProtectedRoute>} />
                <Route path="/service/rental/deleted" element={<ProtectedRoute admin><ServiceDeletedTickets category="rental" /></ProtectedRoute>} />
                <Route path="/service/stock/deleted" element={<ProtectedRoute admin><ServiceDeletedTickets category="stock" /></ProtectedRoute>} />
                <Route path="/service/tickets/:ticketId/edit" element={<ServiceTicketDetail />} />
                <Route path="/service/tickets/:ticketId" element={<ServiceTicketDetail />} />
                <Route path="/service/audit" element={<ServiceAudit />} />
                <Route path="/quotations" element={<ProtectedRoute roles={["staff", "admin"]} fallback={<QuotationAccessDenied />}><QuotationHistory /></ProtectedRoute>} />
                <Route path="/quotations/deleted" element={<ProtectedRoute admin><DeletedQuotationHistory /></ProtectedRoute>} />
                <Route path="/quotations/deleted/:quotationId" element={<ProtectedRoute admin><DeletedQuotationDetail /></ProtectedRoute>} />
                <Route path="/quotations/create" element={<ProtectedRoute roles={["staff", "admin"]} fallback={<QuotationAccessDenied />}><QuotationCreateChoice /></ProtectedRoute>} />
                <Route path="/quotations/create/:quotationType" element={<ProtectedRoute roles={["staff", "admin"]} fallback={<QuotationAccessDenied />}><QuotationFormPage /></ProtectedRoute>} />
                <Route path="/quotations/:quotationId" element={<ProtectedRoute roles={["staff", "admin"]} fallback={<QuotationAccessDenied />}><QuotationFormPage /></ProtectedRoute>} />
                <Route path="/quotations/:quotationId/edit" element={<ProtectedRoute roles={["staff", "admin"]} fallback={<QuotationAccessDenied />}><QuotationFormPage /></ProtectedRoute>} />
                <Route path="/firmware" element={<ProtectedRoute roles={FIRMWARE_ROLES}><Firmware /></ProtectedRoute>} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/audit" element={<Audit />} />
                <Route path="/manage-users" element={<ProtectedRoute admin><ManageUsers /></ProtectedRoute>} />
                <Route path="/master-data/technicians" element={<ProtectedRoute admin><MasterDataPage type="technicians" /></ProtectedRoute>} />
                <Route path="/master-data/device-models" element={<ProtectedRoute admin><MasterDataPage type="device-models" /></ProtectedRoute>} />
              </Route>
            </Routes>
            </ViewModeProvider>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
